import { Emulator } from "./emulator.js";

const AUDIO_PUMP_FRAMES = 512;
const AUDIO_PUMP_INTERVAL_MS = 8;
const AUDIO_TARGET_BACKLOG_FRAMES = 2048;
const AUDIO_MAX_BACKLOG_FRAMES = 4096;
const FALLBACK_SCRIPT_FRAMES = 1024;
const FALLBACK_TARGET_QUEUE_FRAMES = 2048;
const FALLBACK_MAX_QUEUE_FRAMES = 4096;
const FALLBACK_QUEUE_CAPACITY = 8192;

type AudioContextCtor = new (options?: AudioContextOptions) => AudioContext;

export type BrowserAudioMode = "idle" | "audio-worklet" | "script-processor" | "unavailable";

export type BrowserAudioDebugState = {
  mode: BrowserAudioMode;
  reason: string | null;
};

export type BrowserAudioDiagnostics = {
  droppedFrames: number;
  emulatorBacklogFrames: number;
  highWaterFrames: number;
  queueFrames: number;
  underrunCount: number;
};

type AudioWorkletPortMessage =
  | { type: "active"; active: boolean }
  | { type: "config"; sourceRate: number }
  | { type: "frames"; frames: Int16Array }
  | { type: "reset" };

type AudioWorkletFramesMessage = { type: "frames"; frames: Int16Array };
type AudioWorkletStatsMessage = {
  type: "stats";
  droppedFrames: number;
  highWaterFrames: number;
  queueFrames: number;
  underrunCount: number;
};

declare global {
  interface Window {
    webkitAudioContext?: AudioContextCtor;
  }
}

class AudioSampleQueue {
  private readonly samples = new Int16Array(FALLBACK_QUEUE_CAPACITY);
  private head = 0;
  private count = 0;

  get length(): number {
    return this.count;
  }

  pushFrames(frames: Int16Array): void {
    if (frames.length >= FALLBACK_QUEUE_CAPACITY) {
      this.samples.set(frames.subarray(frames.length - FALLBACK_QUEUE_CAPACITY));
      this.head = 0;
      this.count = FALLBACK_QUEUE_CAPACITY;
      return;
    }

    for (let i = 0; i < frames.length; i += 1) {
      this.samples[(this.head + this.count + i) % FALLBACK_QUEUE_CAPACITY] = frames[i];
    }
    this.count += frames.length;
    if (this.count > FALLBACK_QUEUE_CAPACITY) {
      this.head = (this.head + this.count - FALLBACK_QUEUE_CAPACITY) % FALLBACK_QUEUE_CAPACITY;
      this.count = FALLBACK_QUEUE_CAPACITY;
    }
  }

  discardOldest(count: number): void {
    const discard = Math.max(0, Math.min(Math.floor(count), this.count));
    if (discard === 0) {
      return;
    }

    this.head = (this.head + discard) % FALLBACK_QUEUE_CAPACITY;
    this.count -= discard;
  }

  trimToTarget(maxFrames: number, targetFrames: number): number {
    if (this.count <= maxFrames) {
      return 0;
    }

    const discarded = this.count - targetFrames;
    this.discardOldest(discarded);
    return discarded;
  }

  sampleAt(offset: number): number {
    return this.samples[(this.head + offset) % FALLBACK_QUEUE_CAPACITY];
  }
}

export class BrowserAudioBridge {
  private context: AudioContext | null = null;
  private diagnosticsActive = false;
  private emulatorBacklogFrames = 0;
  private emulatorDroppedFrames = 0;
  private workletNode: AudioWorkletNode | null = null;
  private fallbackNode: ScriptProcessorNode | null = null;
  private fallbackDroppedFrames = 0;
  private fallbackHighWaterFrames = 0;
  private fallbackUnderrunCount = 0;
  private readonly queue = new AudioSampleQueue();
  private phase = 0;
  private pumpTimer: number | null = null;
  private sourceRate = 0;
  private startPromise: Promise<void> | null = null;
  private readonly workletFramesMessage: AudioWorkletFramesMessage = {
    type: "frames",
    frames: new Int16Array(0)
  };
  private debugState: BrowserAudioDebugState = {
    mode: "idle",
    reason: "Waiting for user gesture."
  };
  private inactiveDiagnostics: BrowserAudioDiagnostics = {
    droppedFrames: 0,
    emulatorBacklogFrames: 0,
    highWaterFrames: 0,
    queueFrames: 0,
    underrunCount: 0
  };
  private workletDiagnostics: BrowserAudioDiagnostics = {
    droppedFrames: 0,
    emulatorBacklogFrames: 0,
    highWaterFrames: 0,
    queueFrames: 0,
    underrunCount: 0
  };

  constructor(private readonly emulator: Emulator) {}

  isStarted(): boolean {
    return this.context !== null && (this.workletNode !== null || this.fallbackNode !== null);
  }

  getDebugState(): BrowserAudioDebugState {
    return { ...this.debugState };
  }

  getDiagnostics(): BrowserAudioDiagnostics {
    if (!this.diagnosticsActive) {
      return { ...this.inactiveDiagnostics };
    }
    if (this.workletNode !== null) {
      return {
        ...this.workletDiagnostics,
        droppedFrames: this.workletDiagnostics.droppedFrames + this.emulatorDroppedFrames,
        emulatorBacklogFrames: this.emulatorBacklogFrames
      };
    }
    return {
      droppedFrames: this.fallbackDroppedFrames + this.emulatorDroppedFrames,
      emulatorBacklogFrames: this.emulatorBacklogFrames,
      highWaterFrames: this.fallbackHighWaterFrames,
      queueFrames: this.queue.length,
      underrunCount: this.fallbackUnderrunCount
    };
  }

  beginDiagnosticsSession(): void {
    this.diagnosticsActive = true;
    this.inactiveDiagnostics = {
      droppedFrames: 0,
      emulatorBacklogFrames: 0,
      highWaterFrames: 0,
      queueFrames: 0,
      underrunCount: 0
    };
    this.emulatorBacklogFrames = 0;
    this.emulatorDroppedFrames = 0;
    this.fallbackDroppedFrames = 0;
    this.fallbackHighWaterFrames = 0;
    this.fallbackUnderrunCount = 0;
    this.workletDiagnostics = {
      droppedFrames: 0,
      emulatorBacklogFrames: 0,
      highWaterFrames: 0,
      queueFrames: 0,
      underrunCount: 0
    };
    if (this.workletNode !== null) {
      this.workletNode.port.postMessage({ type: "reset" });
      this.workletNode.port.postMessage({ active: true, type: "active" });
    }
  }

  endDiagnosticsSession(): void {
    const current = this.getDiagnostics();
    this.diagnosticsActive = false;
    this.inactiveDiagnostics = {
      ...current,
      emulatorBacklogFrames: 0,
      queueFrames: 0
    };
    this.emulatorBacklogFrames = 0;
    if (this.workletNode !== null) {
      this.workletNode.port.postMessage({ active: false, type: "active" });
    }
  }

  async start(): Promise<void> {
    if (this.startPromise !== null) {
      return this.startPromise;
    }

    this.startPromise = this.startInternal();
    try {
      await this.startPromise;
    } catch (error) {
      this.startPromise = null;
      throw error;
    }
  }

  pump(): void {
    if (!this.isStarted()) {
      return;
    }

    let available = this.dropStaleEmulatorFrames(this.emulator.getAvailableAudioFrames());
    while (available > 0) {
      const frames = this.emulator.copyAudioFrames(Math.min(available, AUDIO_PUMP_FRAMES));
      if (frames.length === 0) {
        break;
      }
      if (this.workletNode !== null) {
        this.workletFramesMessage.frames = frames;
        this.workletNode.port.postMessage(this.workletFramesMessage, [frames.buffer]);
      } else {
        this.enqueueFrames(frames);
      }
      available -= frames.length;
    }
  }

  private ensurePumpTimer(): void {
    if (this.pumpTimer !== null) {
      return;
    }
    this.pumpTimer = window.setInterval(() => {
      this.pump();
    }, AUDIO_PUMP_INTERVAL_MS);
  }

  private enqueueFrames(frames: Int16Array): void {
    this.queue.pushFrames(frames);
    if (this.diagnosticsActive) {
      this.fallbackHighWaterFrames = Math.max(this.fallbackHighWaterFrames, this.queue.length);
    }
    this.trimFallbackQueue();
  }

  private dropStaleEmulatorFrames(available: number): number {
    let remaining = available;
    this.emulatorBacklogFrames = this.diagnosticsActive ? remaining : 0;
    while (remaining > AUDIO_MAX_BACKLOG_FRAMES) {
      const discard = this.emulator.copyAudioFrames(
        Math.min(remaining - AUDIO_TARGET_BACKLOG_FRAMES, AUDIO_PUMP_FRAMES)
      );
      if (discard.length === 0) {
        break;
      }
      if (this.diagnosticsActive) {
        this.emulatorDroppedFrames += discard.length;
      }
      remaining -= discard.length;
    }
    this.emulatorBacklogFrames = this.diagnosticsActive ? remaining : 0;
    return remaining;
  }

  private trimFallbackQueue(): void {
    const discarded = this.queue.trimToTarget(FALLBACK_MAX_QUEUE_FRAMES, FALLBACK_TARGET_QUEUE_FRAMES);
    if (discarded > 0 && this.diagnosticsActive) {
      this.fallbackDroppedFrames += discarded;
    }
    if (discarded > 0) this.phase = 0;
  }

  private fillFallbackOutput(output: Float32Array, outputRate: number): void {
    const step = this.sourceRate / outputRate;

    this.pump();
    let underrun = false;
    for (let i = 0; i < output.length; i += 1) {
      const baseOffset = Math.floor(this.phase);
      const nextOffset = baseOffset + 1;
      if (nextOffset >= this.queue.length) {
        output[i] = 0;
        underrun = true;
        continue;
      }

      const frac = this.phase - baseOffset;
      const s0 = this.queue.sampleAt(baseOffset) / 32768;
      const s1 = this.queue.sampleAt(nextOffset) / 32768;
      output[i] = s0 + (s1 - s0) * frac;
      this.phase += step;

      const consumed = Math.floor(this.phase);
      if (consumed > 0) {
        this.queue.discardOldest(consumed);
        this.phase -= consumed;
      }
    }
    if (underrun && this.diagnosticsActive) {
      this.fallbackUnderrunCount += 1;
    }
  }

  private getAudioContextCtor(): AudioContextCtor | null {
    return window.AudioContext ?? window.webkitAudioContext ?? null;
  }

  private setDebugState(mode: BrowserAudioMode, reason: string | null): void {
    this.debugState = { mode, reason };
  }

  private describeError(error: unknown, fallbackMessage: string): string {
    if (error instanceof Error && error.message.trim() !== "") {
      return error.message.trim();
    }
    if (typeof error === "string" && error.trim() !== "") {
      return error.trim();
    }
    return fallbackMessage;
  }

  private getWorkletUnavailableReason(): string {
    if (!window.isSecureContext) {
      return "AudioWorklet needs HTTPS or localhost; this page is not running in a secure context.";
    }
    return "AudioWorklet is unavailable in this browser or context.";
  }

  private async createWorkletNode(context: AudioContext): Promise<boolean> {
    if (typeof context.audioWorklet?.addModule !== "function") {
      return false;
    }

    await context.audioWorklet.addModule(new URL("./audio-worklet.js", import.meta.url));
    this.workletNode = new AudioWorkletNode(context, "am8-audio-processor");
    this.workletNode.connect(context.destination);
    this.workletNode.port.onmessage = (event: MessageEvent<AudioWorkletStatsMessage>) => {
      const data = event.data;
      if (data?.type !== "stats") {
        return;
      }
      this.workletDiagnostics = {
        droppedFrames: data.droppedFrames,
        emulatorBacklogFrames: this.diagnosticsActive ? this.emulatorBacklogFrames : 0,
        highWaterFrames: data.highWaterFrames,
        queueFrames: data.queueFrames,
        underrunCount: data.underrunCount
      };
    };
    const message: AudioWorkletPortMessage = {
      type: "config",
      sourceRate: this.emulator.getAudioSourceRate()
    };
    this.workletNode.port.postMessage(message);
    this.setDebugState("audio-worklet", null);
    return true;
  }

  private createFallbackNode(context: AudioContext, reason: string): void {
    if (typeof context.createScriptProcessor !== "function") {
      throw new Error("ScriptProcessor fallback is unavailable in this browser.");
    }

    const fallback = context.createScriptProcessor(FALLBACK_SCRIPT_FRAMES, 0, 1);

    fallback.onaudioprocess = (event: AudioProcessingEvent) => {
      this.fillFallbackOutput(event.outputBuffer.getChannelData(0), event.outputBuffer.sampleRate);
    };
    fallback.connect(context.destination);
    this.fallbackNode = fallback;
    this.setDebugState("script-processor", reason);
  }

  private async startInternal(): Promise<void> {
    if (this.context === null) {
      const AudioCtor = this.getAudioContextCtor();
      if (AudioCtor === null) {
        this.setDebugState("unavailable", "Web Audio is unavailable in this browser.");
        throw new Error("Web Audio is unavailable in this browser.");
      }

      this.context = new AudioCtor({ latencyHint: "interactive" });
      this.sourceRate = this.emulator.getAudioSourceRate();
      if (this.context.state !== "running") {
        await this.context.resume();
      }
      let workletReady = false;
      let fallbackReason = this.getWorkletUnavailableReason();
      try {
        workletReady = await this.createWorkletNode(this.context);
      } catch (error) {
        fallbackReason = this.describeError(error, "AudioWorklet failed to initialize.");
        console.warn("audio: worklet init failed, falling back to ScriptProcessor", error);
      }
      if (!workletReady) {
        try {
          this.createFallbackNode(this.context, fallbackReason);
        } catch (error) {
          const message = this.describeError(error, "ScriptProcessor fallback is unavailable.");
          this.setDebugState("unavailable", message);
          throw error;
        }
      }
    }

    if (this.context.state !== "running") {
      await this.context.resume();
    }
    this.ensurePumpTimer();
    this.pump();
  }
}
